import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccessKeys } from '@/components/access-keys';
import { AccessKeyCreateModal } from '@/components/access-keys/access-key-create-modal';
import { Loading } from '@/components/loading';
import { useNow } from '@/hooks/use-now';
import {
  useMeQuery,
  useNewAccessKeyMutation,
  useDeleteAccessKeyMutation,
} from '@/api/user';
import { AccessKeySecret } from '@/types';

const SettingsAccessKeys = () => {
  const now = useNow();
  const navigate = useNavigate();
  const { data: me, isLoading } = useMeQuery();
  const [createNewAccessKey, { isLoading: isCreating }] =
    useNewAccessKeyMutation();
  const [deleteAccessKey] = useDeleteAccessKeyMutation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAccessKey, setNewAccessKey] = useState<
    undefined | AccessKeySecret
  >();
  const onNewAccessKey = useCallback(() => {
    setShowCreateModal(true);
  }, []);
  const onCancelCreate = useCallback(() => {
    setShowCreateModal(false);
  }, []);
  const onCreateWithScopes = useCallback(
    async (name: string, scopes: string[], expiresAt: string | null) => {
      setShowCreateModal(false);
      setNewAccessKey(
        await createNewAccessKey({ name, scopes, expiresAt }).unwrap(),
      );
    },
    [createNewAccessKey],
  );
  const onDismissNewAccessKey = useCallback(() => {
    setNewAccessKey(undefined);
  }, [setNewAccessKey]);

  const onDeleteAccessKey = useCallback(
    (id: string) => {
      deleteAccessKey(id);
    },
    [deleteAccessKey],
  );

  const onClickAccessKey = useCallback(
    (id: string) => {
      navigate(`/settings/account/access-keys/${id}`);
    },
    [navigate],
  );

  if (!isLoading && me) {
    return (
      <>
        {showCreateModal && (
          <AccessKeyCreateModal
            onCancel={onCancelCreate}
            onCreate={onCreateWithScopes}
            isCreating={isCreating}
          />
        )}
        <AccessKeys
          now={now}
          keys={me.keys}
          isCreating={isCreating}
          newAccessKey={newAccessKey}
          onNewAccessKey={onNewAccessKey}
          onDeleteAccessKey={onDeleteAccessKey}
          onClickAccessKey={onClickAccessKey}
          onDismissNewAccessKey={onDismissNewAccessKey}
        />
      </>
    );
  } else {
    return <Loading />;
  }
};

export { SettingsAccessKeys };
